/**
 * PTBFRollingFormDialog — Create / Edit PTBF Rolling.
 *
 * 1:1 Access port of `frmPurchaseQryPTBFRollingAdd`.
 * Visual style mirrors `saleContract/SaleAmendmentFormDialog` (compact
 * fonts, inline Save/Cancel in DialogTitle, Grid sections with uppercase
 * caption headers, readonly fields shaded via `roSx`).
 *
 * Sections:
 *   1. Rolling Information — editable rolling fields + calculated New Diff
 *   2. Contract Reference  — read-only from selected contract
 *   3. Amendment Information — auto-generated preview + editable note
 *
 * Calculated New Diff = old_diffs + spread − rolling_fee (Access new_spreads).
 * Edit mode disables rolling_date + contract_no (Access btnEdit_Click).
 */
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { usePagePermission } from '@/hooks/usePagePermission'
import { useSnackbar } from '@/hooks/useSnackbar'
import { useListAnnexesQuery } from '@/store/api/purchaseContractApi'
import { useGetLocalPQuery, useListLocalPQuery } from '@/store/api/purchaseContractApi'
import { useListFuturesMonthsQuery } from '@/store/api/lookupsApi'
import {
  useCreatePTBFRollingMutation,
  useUpdatePTBFRollingMutation,
} from '@/store/api/purchaseContractApi'
import type { LocalPListItem, PTBFRollingCreateIn, PTBFRollingOut } from '@/types/api'
import { HIDE_ON_MOBILE_INLINE } from '@/theme/responsive'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  instructionAlertSx,
  numSx,
  roSx,
  sectionHeaderSx,
} from './dialogStyle'

export interface PTBFRollingFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  fixedContractNo?: string
  initialValue?: PTBFRollingOut | null
  onClose: () => void
  onSaved?: (id: number) => void
}

interface RollingFormValues {
  contract_no: string
  rolling_date: string
  lot_to_roll: number | ''
  old_futures_month: string
  new_futures_month: string
  spread: number | ''
  rolling_fee: number | ''
  quantity_kg: number | ''
  old_diffs: number | ''
  note: string
  amendment_note: string
}

const EMPTY: RollingFormValues = {
  contract_no: '',
  rolling_date: dayjs().format('YYYY-MM-DD'),
  lot_to_roll: '',
  old_futures_month: '',
  new_futures_month: '',
  spread: '',
  rolling_fee: '',
  quantity_kg: '',
  old_diffs: '',
  note: '',
  amendment_note: '',
}

// DB column types: lot_to_roll/spread/rolling_fee/old_diffs = integer;
// quantity_kg = double precision. Yup rejects non-integer for the int fields
// so user lỗi sớm (thay vì để Pydantic silent truncate khi save).
const isInt = (v: number | '' | undefined | null): boolean =>
  typeof v === 'number' && Number.isInteger(v)

const schema: yup.ObjectSchema<RollingFormValues> = yup.object({
  contract_no: yup.string().trim().required('Contract No is required'),
  rolling_date: yup.string().required('Rolling Date is required'),
  lot_to_roll: yup
    .mixed<number | ''>()
    .test('required', 'Lot to roll is required', (v) => v !== '' && v !== undefined && v !== null)
    .test('integer', 'Lot to roll must be a whole number', isInt)
    .test('positive', 'Lot to roll must be > 0', (v) => typeof v === 'number' && v > 0)
    .required(),
  old_futures_month: yup.string().trim().required('Old Futures Month is required'),
  new_futures_month: yup
    .string()
    .trim()
    .required('New Futures Month is required')
    .test('different', 'New Futures Month must differ from Old Futures Month', function (v) {
      const old = this.parent.old_futures_month as string
      if (!v || !old) return true
      return v !== old
    }),
  spread: yup
    .mixed<number | ''>()
    .test('required', 'Spread is required', (v) => v !== '' && v !== undefined && v !== null)
    .test('integer', 'Spread must be a whole number', isInt)
    .required(),
  rolling_fee: yup
    .mixed<number | ''>()
    .test('required', 'Rolling Fee is required', (v) => v !== '' && v !== undefined && v !== null)
    .test('integer', 'Rolling Fee must be a whole number', isInt)
    .required(),
  quantity_kg: yup.mixed<number | ''>().default(''),
  old_diffs: yup
    .mixed<number | ''>()
    .test('integer', 'Old Diffs must be a whole number', (v) => v === '' || v == null || isInt(v))
    .default(''),
  note: yup.string().trim().default(''),
  amendment_note: yup.string().trim().default(''),
}) as yup.ObjectSchema<RollingFormValues>

function fromRolling(r: PTBFRollingOut): RollingFormValues {
  const n = (x: number | null | undefined): number | '' =>
    x === null || x === undefined ? '' : x
  const s = (x: string | null | undefined): string => x ?? ''
  return {
    contract_no: s(r.contract_no),
    rolling_date: s(r.rolling_date),
    lot_to_roll: n(r.lot_to_roll),
    old_futures_month: s(r.old_futures_month),
    new_futures_month: s(r.new_futures_month),
    spread: n(r.spread),
    rolling_fee: n(r.rolling_fee),
    quantity_kg: n(r.quantity_kg),
    old_diffs: n(r.old_diffs),
    note: s(r.note),
    amendment_note: '',
  }
}

function toApi(v: RollingFormValues): PTBFRollingCreateIn {
  const str = (x: string): string | null => (x.trim() === '' ? null : x.trim())
  // BE Pydantic schema khai báo các field này là int → coerce trước khi send
  // (Math.trunc thay vì Math.round để khớp behavior Pydantic int() cast).
  const toInt = (x: number | ''): number | null =>
    typeof x === 'number' ? Math.trunc(x) : null
  return {
    contract_no: v.contract_no.trim(),
    rolling_date: v.rolling_date,
    lot_to_roll: toInt(v.lot_to_roll) ?? 0,
    old_futures_month: v.old_futures_month,
    new_futures_month: v.new_futures_month,
    spread: toInt(v.spread) ?? 0,
    rolling_fee: toInt(v.rolling_fee) ?? 0,
    quantity_kg: typeof v.quantity_kg === 'number' ? v.quantity_kg : null,
    old_diffs: toInt(v.old_diffs),
    note: str(v.note),
    amendment_note: str(v.amendment_note),
  }
}

const numOr0 = (v: number | '' | null | undefined): number =>
  typeof v === 'number' ? v : 0

// Access "Standard" format = #,##0.00 (2 decimals, thousands separator).
const formatStandard = (v: number | null | undefined): string =>
  v == null || Number.isNaN(v)
    ? ''
    : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// Access date display format = DD-MMM-YYYY (e.g., 05-Mar-2026).
const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatAccessDate = (v: unknown): string => {
  if (!v) return ''
  const d = new Date(String(v))
  if (Number.isNaN(d.getTime())) return String(v)
  return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

// ICE futures month-code → tháng số (1-12). Access DLookUp từ public_market_month
// trả về cột `month` integer, không phải tên tháng English. KCE = Arabica (US),
// LRC = Robusta (EU) — cùng mã chuẩn ngành nên map dùng chung.
const FUTURES_MONTH_MAP: Record<string, number> = {
  F: 1, G: 2, H: 3, J: 4, K: 5, M: 6,
  N: 7, Q: 8, U: 9, V: 10, X: 11, Z: 12,
}

interface FuturesMonthInfo {
  exchange: string
  month: string
  year: string
}

function describeFuturesMonth(symbol: string | null | undefined): FuturesMonthInfo | null {
  if (!symbol) return null
  const m = /^([A-Z]+)([A-Z])(\d{2})$/.exec(symbol)
  if (!m) return null
  const [, mkt, mcode, yy] = m
  const exchange = mkt.startsWith('LRC') ? 'ICE Europe Robusta' : 'ICE US Arabica'
  const monthNum = FUTURES_MONTH_MAP[mcode]
  return {
    exchange,
    month: monthNum != null ? String(monthNum) : mcode,
    year: `20${yy}`,
  }
}

// Mirror Access content_before_change / content_after_change ControlSource
// (form_frmPurchaseQryPTBFRollingAdd.txt line 2679-2684 / 2763-2768):
//   "Giá: Giá thị trường {ICE Europe Robusta|ICE US Arabica} tháng {N} năm 20{YY}{ trừ|cộng |abs|}{unit}."
// Spacing chuẩn xác theo Access (space-trừ-space, không có space giữa diffs và unit).
function buildContentText(
  futuresMonth: string,
  diffs: number,
  priceUnit: string,
): string {
  const info = describeFuturesMonth(futuresMonth)
  if (!info) return ''
  const diffPart = diffs < 0 ? ` trừ ${Math.abs(diffs)} ` : ` cộng ${Math.abs(diffs)} `
  return `Giá: Giá thị trường ${info.exchange} tháng ${info.month} năm ${info.year}${diffPart}${priceUnit}.`
}

const INSTRUCTION_INSERT = `When a new rolling is created, the system will:
(1) Update the New Diff and the new Futures Month in the Purchase Contract.
(2) Automatically generate a new amendment.`

const INSTRUCTION_UPDATE = `When this rolling is updated, the system will:
(1) Update the New Diff and the new Futures Month in the Purchase Contract.
(2) Automatically update the associated amendment.`

export function PTBFRollingFormDialog({
  open,
  mode,
  fixedContractNo,
  initialValue,
  onClose,
  onSaved,
}: PTBFRollingFormDialogProps) {
  const snack = useSnackbar()
  const { canEdit } = usePagePermission()
  const isEdit = mode === 'edit'

  const [createRolling, { isLoading: creating }] = useCreatePTBFRollingMutation()
  const [updateRolling, { isLoading: updating }] = useUpdatePTBFRollingMutation()
  const loading = creating || updating

  const { data: futuresMonths = [] } = useListFuturesMonthsQuery()

  const { data: contractsList } = useListLocalPQuery({
    contract_type: 'Differential',
    is_status: 1,
    page: 1,
    page_size: 1_000_000,
  })
  const contractOptions = useMemo(() => contractsList?.items ?? [], [contractsList])

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RollingFormValues>({
    resolver: yupResolver(schema),
    defaultValues: EMPTY,
  })

  const selectedContractNo = watch('contract_no')
  const oldFuturesMonth = watch('old_futures_month')
  const newFuturesMonth = watch('new_futures_month')
  const oldDiffsVal = watch('old_diffs')
  const spreadVal = watch('spread')
  const rollingFeeVal = watch('rolling_fee')
  const rollingDateVal = watch('rolling_date')

  const { data: contractDetailRes } = useGetLocalPQuery(selectedContractNo, {
    skip: !selectedContractNo,
  })
  const contractDetail = contractDetailRes?.data

  const contractListRow: LocalPListItem | undefined = useMemo(
    () => contractOptions.find((c) => c.contract_no === selectedContractNo),
    [contractOptions, selectedContractNo],
  )

  // Fetch all annexes của contract một lần — vừa dùng để suggest annex_no
  // mới (create), vừa dùng để DLookup annex_note hiện tại (edit). Khớp Access
  // btnEdit_Click line 976: `cleanCheck(DLookup("note", "public_annex_p",
  // "is_status=0 and annex_no='" & ref_amendment & "'"))`.
  const { data: annexesForContract } = useListAnnexesQuery(
    selectedContractNo ? { contract_no: selectedContractNo, page: 1, page_size: 200 } : undefined,
    { skip: !selectedContractNo },
  )

  // Legacy convention {contract_no}/PL{NN} — phải khớp BE `_next_pl_annex_no`.
  const suggestedAmendmentNo = useMemo(() => {
    if (isEdit) return ''
    if (!selectedContractNo) return ''
    const items = annexesForContract?.items ?? []
    const pat = new RegExp(
      `^${selectedContractNo.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}/PL(\\d+)$`,
    )
    let maxSeq = 0
    for (const a of items) {
      const m = a.annex_no?.match(pat)
      if (m) {
        const n = Number(m[1])
        if (Number.isFinite(n) && n > maxSeq) maxSeq = n
      }
    }
    return `${selectedContractNo}/PL${String(maxSeq + 1).padStart(2, '0')}`
  }, [isEdit, selectedContractNo, annexesForContract])

  // Load amendment_note hiện tại (edit mode) từ annex link qua ref_amendment.
  const existingAmendmentNote = useMemo(() => {
    if (!isEdit || !initialValue?.ref_amendment) return ''
    const items = annexesForContract?.items ?? []
    return items.find((a) => a.annex_no === initialValue.ref_amendment)?.note ?? ''
  }, [isEdit, initialValue?.ref_amendment, annexesForContract])

  useEffect(() => {
    if (!open || !isEdit) return
    setValue('amendment_note', existingAmendmentNote)
  }, [open, isEdit, existingAmendmentNote, setValue])

  const newDiffs = useMemo(
    () => numOr0(oldDiffsVal) + numOr0(spreadVal) - numOr0(rollingFeeVal),
    [oldDiffsVal, spreadVal, rollingFeeVal],
  )

  const priceUnit = contractDetail?.price_unit ?? contractListRow?.price_unit ?? ''
  const contentBefore = useMemo(
    () => buildContentText(oldFuturesMonth, numOr0(oldDiffsVal), priceUnit),
    [oldFuturesMonth, oldDiffsVal, priceUnit],
  )
  const contentAfter = useMemo(
    () => buildContentText(newFuturesMonth, newDiffs, priceUnit),
    [newFuturesMonth, newDiffs, priceUnit],
  )

  useEffect(() => {
    if (!open) return
    if (isEdit && initialValue) {
      reset(fromRolling(initialValue))
    } else {
      reset({ ...EMPTY, contract_no: fixedContractNo ?? '' })
    }
  }, [open, isEdit, initialValue, fixedContractNo, reset])

  // Auto-fill from selected contract on create (Access btnNew_Click).
  useEffect(() => {
    if (isEdit) return
    if (!selectedContractNo) return
    if (!contractDetail && !contractListRow) return

    setValue('old_diffs', contractDetail?.ptbf_diffs ?? contractListRow?.ptbf_diffs ?? '')
    setValue('old_futures_month', contractDetail?.futures_month ?? contractListRow?.futures_month ?? '')
    if (contractListRow?.total_lot != null) setValue('lot_to_roll', contractListRow.total_lot)
    const qty = contractDetail?.quantity_kg ?? contractListRow?.quantity_kg
    if (qty != null) setValue('quantity_kg', qty)
  }, [isEdit, selectedContractNo, contractDetail, contractListRow, setValue])

  const cpDisplay = useMemo(() => {
    if (!contractDetail && !contractListRow) return ''
    const code = contractDetail?.supplier_code ?? contractListRow?.supplier_code ?? ''
    const name =
      contractDetail?.supplier_name ??
      contractDetail?.short_name ??
      contractListRow?.supplier_name ??
      ''
    return `${code}${name ? ` — ${name}` : ''}`
  }, [contractDetail, contractListRow])

  const onSubmit = handleSubmit(
    async (values) => {
      const payload = toApi(values)
      try {
        if (isEdit && initialValue) {
          const res = await updateRolling({ id: initialValue.id, body: payload }).unwrap()
          snack.success('Rolling updated')
          onSaved?.(res.data?.id ?? initialValue.id)
        } else {
          const res = await createRolling(payload).unwrap()
          snack.success('Rolling created (auto-Amendment)')
          onSaved?.(res.data?.id ?? 0)
        }
        onClose()
      } catch (err) {
        snack.error(
          getErrorMessage(err, isEdit ? 'Failed to update rolling' : 'Failed to create rolling'),
        )
      }
    },
    () => snack.error('Please fix the highlighted fields'),
  )

  const renderNumberField = (
    field: { value: number | ''; onChange: (v: number | '') => void; onBlur?: () => void },
    label: string,
    opts?: { step?: string; disabled?: boolean; required?: boolean; error?: boolean; helperText?: string },
  ) => (
    <TextField
      label={`${label}${opts?.required ? ' *' : ''}`}
      type="number"
      size="small"
      fullWidth
      value={field.value === '' ? '' : field.value}
      onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
      onBlur={field.onBlur}
      inputProps={{ step: opts?.step, inputMode: 'decimal' }}
      disabled={opts?.disabled}
      error={opts?.error}
      helperText={opts?.helperText}
      InputLabelProps={opts?.disabled ? { shrink: true } : undefined}
      sx={opts?.disabled ? [numSx, roSx] : [numSx]}
    />
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap title="Purchase - PTBF Rolling" sx={dialogTitleTextSx}>
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Purchase - </Box>PTBF Rolling
          {isEdit && initialValue?.id ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              #{initialValue.id}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            variant="contained"
            size="small"
            color="success"
            onClick={() => void onSubmit()}
            disabled={loading || !canEdit}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button variant="outlined" size="small" onClick={onClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {/* ── 1) Rolling Information ───────────────────────────────────── */}
        <Box sx={{ mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={sectionHeaderSx}>
            Rolling Information
          </Typography>
          <Divider sx={{ mt: 0.5 }} />
        </Box>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          {/* Row 1: 3 | 6 | 3 */}
          <Grid item xs={12} sm={3}>
            <Controller
              control={control}
              name="rolling_date"
              render={({ field }) => (
                <DatePicker
                  label="Rolling Date *"
                  format="DD-MMM-YYYY"
                  disabled={isEdit}
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(d) => field.onChange(d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      error: !!errors.rolling_date,
                      helperText: errors.rolling_date?.message,
                      sx: isEdit ? roSx : undefined,
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {fixedContractNo ? (
              <TextField
                label="Contract No."
                size="small"
                fullWidth
                value={fixedContractNo}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={roSx}
              />
            ) : (
              <Controller
                control={control}
                name="contract_no"
                render={({ field }) => {
                  const found = contractOptions.find((c) => c.contract_no === field.value)
                  const selected =
                    found ??
                    (field.value
                      ? ({
                          contract_no: field.value,
                          supplier_name: null,
                          supplier_code: null,
                        } as (typeof contractOptions)[number])
                      : null)
                  // inject sel vào options → tắt MUI "none of options match" (anti-pattern #46)
                  const contractAcOpts =
                    selected && !contractOptions.some((o) => o.contract_no === selected.contract_no)
                      ? [selected, ...contractOptions]
                      : contractOptions
                  return (
                    <Autocomplete
                      size="small"
                      options={contractAcOpts}
                      value={selected}
                      onChange={(_e, v) => field.onChange(v?.contract_no ?? '')}
                      getOptionLabel={(opt) =>
                        opt ? `${opt.contract_no} — ${opt.supplier_name ?? opt.supplier_code ?? ''}` : ''
                      }
                      isOptionEqualToValue={(o, v) => o.contract_no === v.contract_no}
                      disabled={isEdit}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Contract No. *"
                          error={!!errors.contract_no}
                          helperText={errors.contract_no?.message}
                          sx={isEdit ? roSx : undefined}
                        />
                      )}
                    />
                  )
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={3}>
            <Controller
              control={control}
              name="new_futures_month"
              render={({ field }) => {
                // Legacy data có thể lưu short code (vd "N26") trong khi lookup
                // chỉ trả full symbol ("LRCN26", "KCEN26"). Nếu value hiện tại
                // không khớp option nào → dùng sel ảo để hiện code legacy +
                // tránh Autocomplete "no match".
                const sel = futuresMonths.find((m) => m.value === field.value)
                  ?? (field.value ? { value: field.value, label: `${field.value} (legacy)` } : null)
                // inject sel vào options → tắt MUI "none of options match" (anti-pattern #46)
                const futuresAcOpts =
                  sel && !futuresMonths.some((o) => o.value === sel.value)
                    ? [sel, ...futuresMonths]
                    : futuresMonths
                return (
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={futuresAcOpts}
                    value={sel}
                    onChange={(_e, v) => field.onChange(v?.value ?? '')}
                    getOptionLabel={(o) => o ? (o.value === o.label ? o.value : `${o.value} — ${o.label ?? ''}`) : ''}
                    isOptionEqualToValue={(a, b) => a.value === b.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="New Futures Month *"
                        error={!!errors.new_futures_month}
                        helperText={errors.new_futures_month?.message}
                      />
                    )}
                  />
                )
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Supplier"
              size="small"
              fullWidth
              value={cpDisplay}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={roSx}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Controller
              control={control}
              name="old_futures_month"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Old Futures Month"
                  size="small"
                  fullWidth
                  disabled
                  InputLabelProps={{ shrink: true }}
                  sx={roSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Controller
              control={control}
              name="old_diffs"
              render={({ field }) =>
                renderNumberField(field, 'Old Diffs', { step: '1', disabled: true })
              }
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Controller
              control={control}
              name="lot_to_roll"
              render={({ field }) =>
                renderNumberField(field, 'Lot to roll', {
                  required: true,
                  error: !!errors.lot_to_roll,
                  helperText: errors.lot_to_roll?.message,
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Controller
              control={control}
              name="quantity_kg"
              render={({ field }) => renderNumberField(field, 'Quantity (Kg)', { step: '1' })}
            />
          </Grid>

          {/* Row 4: 3 | 3 | 6 (New Diff wider for helper text) */}
          <Grid item xs={12} sm={3}>
            <Controller
              control={control}
              name="spread"
              render={({ field }) =>
                renderNumberField(field, 'Spread', {
                  step: '1',
                  required: true,
                  error: !!errors.spread,
                  helperText: errors.spread?.message,
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Controller
              control={control}
              name="rolling_fee"
              render={({ field }) =>
                renderNumberField(field, 'Rolling Fee', {
                  step: '1',
                  required: true,
                  error: !!errors.rolling_fee,
                  helperText: errors.rolling_fee?.message,
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="New Diff"
              size="small"
              fullWidth
              value={formatStandard(newDiffs)}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              helperText="= Old Diffs + Spread − Rolling Fee"
              sx={[numSx, roSx]}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              control={control}
              name="note"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Note"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  error={!!errors.note}
                  helperText={errors.note?.message}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* ── 2) Contract Reference (read-only) ────────────────────────── */}
        <Box sx={{ mt: 2, mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={sectionHeaderSx}>
            Contract Reference
          </Typography>
          <Divider sx={{ mt: 0.5 }} />
        </Box>
        {/* 5 field/1 hàng, mỗi field flex:1 → tổng width = width form, không extend. */}
        <Stack direction="row" spacing={1.5} sx={{ mt: 0 }}>
          <TextField
            label="Contract Quantity (kg)"
            size="small"
            fullWidth
            value={
              contractListRow?.quantity_kg != null
                ? formatStandard(contractListRow.quantity_kg)
                : contractDetail?.quantity_kg != null
                ? formatStandard(contractDetail.quantity_kg)
                : ''
            }
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={[numSx, roSx, { flex: 1, minWidth: 0 }]}
          />
          <TextField
            label="No of Bags"
            size="small"
            fullWidth
            value={
              contractListRow?.no_of_bag != null
                ? formatStandard(contractListRow.no_of_bag)
                : contractDetail?.no_of_bag != null
                ? formatStandard(contractDetail.no_of_bag)
                : ''
            }
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={[numSx, roSx, { flex: 1, minWidth: 0 }]}
          />
          <TextField
            label="Lot Fixed"
            size="small"
            fullWidth
            value={contractListRow?.lot_fixed != null ? formatStandard(contractListRow.lot_fixed) : ''}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={[numSx, roSx, { flex: 1, minWidth: 0 }]}
          />
          <TextField
            label="Total Lots"
            size="small"
            fullWidth
            value={contractListRow?.total_lot != null ? formatStandard(contractListRow.total_lot) : ''}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={[numSx, roSx, { flex: 1, minWidth: 0 }]}
          />
          <TextField
            label="Price Unit"
            size="small"
            fullWidth
            value={priceUnit}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            sx={[roSx, { flex: 1, minWidth: 0 }]}
          />
        </Stack>

        {/* ── 3) Amendment Information ─────────────────────────────────── */}
        <Box sx={{ mt: 2, mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={sectionHeaderSx}>
            Amendment Information
          </Typography>
          <Divider sx={{ mt: 0.5 }} />
        </Box>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Amendment No"
              size="small"
              fullWidth
              value={isEdit ? initialValue?.ref_amendment ?? '' : suggestedAmendmentNo}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              helperText={
                !isEdit
                  ? suggestedAmendmentNo
                    ? 'Suggested — final value generated on save'
                    : 'Auto-generated on save'
                  : undefined
              }
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Amendment Date"
              size="small"
              fullWidth
              value={formatAccessDate(rollingDateVal)}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Content before Change"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={contentBefore}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Content after Change"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={contentAfter}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="amendment_note"
              render={({ field }) => (
                <TextField {...field} label="Amendment Note" size="small" fullWidth multiline minRows={2} />
              )}
            />
          </Grid>
        </Grid>

        <Alert severity="info" sx={instructionAlertSx}>
          {isEdit ? INSTRUCTION_UPDATE : INSTRUCTION_INSERT}
        </Alert>
      </DialogContent>
    </Dialog>
  )
}

export default PTBFRollingFormDialog
